import { useState } from "react";
import { useUserInfo } from "../../../../context/user";
import { Pencil, Eye, EyeOff } from "lucide-react";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import EditFieldModal from "./components/EditFieldModal";
import ChangePasswordDialog from "./components/ChangePasswordDialog";
import toast from "react-hot-toast";
import { useUpdateChatUser, useUpdateChatUserPassword } from "../../../../queries/settingsAccount";


function ProfileContent() {
  const user = useUserInfo()[0];
  const updateUser = useUserInfo()[1];
  const userActiveLocation = useUserInfo()[4];
  const [formData, setFormData] = useState();
  const [modalOpen, setModalOpen] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [open, setOpen] = useState(false);
  const updateChatUser = useUpdateChatUser();
  const updateChatUserPassword = useUpdateChatUserPassword();

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleEditClick = (field) => {
    setActiveField(field);
    setModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = await updateChatUser.mutateAsync({
        _id: user._id,
        userName: e.target.userName.value,
        email: e.target.email.value,
        contactNumber: e.target.contactNumber.value,
      });

      if (data) {
        updateUser(data);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred while updating user");
      toast.error("Please try again later");
    }
  };

  const handlePasswordChange = async (passwordData) => {
    try {
      const data = await updateChatUserPassword.mutateAsync({
        _id: user._id,
        old_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
        c_new_password: passwordData.newPassword,
      });
      if (data) {
        toast.success("Password updated successfully");
        updateUser(data);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred while updating user");
      toast.error("Please try again later");
    }
  };

  return (
    <div className="">
      <form className="" onSubmit={(e) => handleSubmit(e)}>
        <div className="page-header [&_.page-display]:text-3xl [&_.page-display]:md:text-4xl [&_.page-lede]:text-sm pb-4">
          <span className="page-eyebrow">Configure</span>
          <h1 className="page-display">
            Profile. <span className="accent block md:inline">How Maybe* knows you.</span>
          </h1>
          <p className="page-lede">Update your name, business, and sign-in details. Changes apply across every Task you delegate.</p>
        </div>
        <div className="max-w-3xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="flex flex-col gap-2">
              <Label htmlFor="userName" className="text-sm text-muted-foreground font-normal">
                User Name
              </Label>
              <div className="interactive-field flex items-center gap-1 rounded-md border border-border bg-card h-11 pl-3 pr-1.5">
                <Input
                  type="text"
                  id="userName"
                  name="userName"
                  value={user?.userName || ""}
                  onChange={(e) => handleChange(e)}
                  className="flex-1 h-auto !p-0 text-sm bg-transparent !border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                />
                <button
                  type="button"
                  aria-label="Edit User Name"
                  onClick={() => handleEditClick("userName")}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-xs font-medium text-foreground transition-colors hover:border-foreground/40 hover:bg-muted"
                >
                  <Pencil className="h-3 w-3" />
                  Edit
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="business_name" className="text-sm text-muted-foreground font-normal">
                Business Name
              </Label>
              <div className="interactive-field flex items-center gap-1 rounded-md border border-border bg-card h-11 pl-3 pr-1.5">
                <Input
                  type="text"
                  id="business_name"
                  name="business_name"
                  value={userActiveLocation?.business_name || ""}
                  onChange={(e) => handleChange(e)}
                  className="flex-1 h-auto !p-0 text-sm bg-transparent !border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                />
                <button
                  type="button"
                  aria-label="Edit Business Name"
                  onClick={() => handleEditClick("business_name")}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-xs font-medium text-foreground transition-colors hover:border-foreground/40 hover:bg-muted"
                >
                  <Pencil className="h-3 w-3" />
                  Edit
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="email" className="text-sm text-muted-foreground font-normal">
                Email
              </Label>
              <div className="interactive-field flex items-center gap-1 rounded-md border border-border bg-card h-11 pl-3 pr-1.5">
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={user?.email || ""}
                  onChange={(e) => handleChange(e)}
                  className="flex-1 h-auto !p-0 text-sm bg-transparent !border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                />
                <button
                  type="button"
                  aria-label="Edit Email"
                  onClick={() => handleEditClick("email")}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-xs font-medium text-foreground transition-colors hover:border-foreground/40 hover:bg-muted"
                >
                  <Pencil className="h-3 w-3" />
                  Edit
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="password" className="text-sm text-muted-foreground font-normal">
                Password
              </Label>
              <div className="interactive-field flex items-center gap-1 rounded-md border border-border bg-card h-11 pl-3 pr-1.5">
                <Input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={user?.assign_password || ""}
                  disabled
                  className="flex-1 h-auto !p-0 text-sm bg-transparent !border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                />
                <button
                  type="button"
                  aria-label="Toggle password visibility"
                  onClick={togglePassword}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>

      <div className="max-w-3xl flex justify-end mt-4">
        <Button
          variant="ghost"
          onClick={() => setOpen(true)}
          className="h-9 px-3 text-sm font-semibold text-foreground hover:bg-muted"
        >
          Change Password
        </Button>
      </div>

      <EditFieldModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        field={activeField}
        label={
          activeField === "userName"
            ? "Username"
            : activeField === "business_name"
              ? "Business Name"
              : "Email"
        }
        user={user}
        updateUser={updateUser}
        userActiveLocation={userActiveLocation}
      />

      <ChangePasswordDialog
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handlePasswordChange}
      />
    </div>
  );
}
export default ProfileContent;
